import React, { useEffect, useState } from "react";
import { CheckCircle2, Download, Lock, X, Sparkles, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { TRUST } from "../lib/content";
import { useCounter } from "../lib/useCounter";
import { generateCertificate } from "../lib/certificate";
import SectionTitle from "../components/site/SectionTitle";
import GeometricDivider from "../components/site/GeometricDivider";
import AnimatedCount from "../components/site/AnimatedCount";

const FAQS = [
  {
    q: "Is there a fee to join?",
    a: "No. The Mecca Majlis is a community program, open to all who join in faith. There is no charge, no tier, no subscription.",
  },
  {
    q: "Are my details stored anywhere?",
    a: "No. Your name, faryad, email and city stay in your browser only. The server is told one thing — to add 1 to the community counter. Nothing personal is sent or stored.",
  },
  {
    q: "What does the certificate contain?",
    a: "Your name, your devotee number, the Gregorian and Hijri dates of joining, the trust seal, and the honorific of Hazrat Sufi Sarkar. Your faryad is intentionally NOT printed — that remains between you and Sarkar.",
  },
  {
    q: "Can I rejoin or share it?",
    a: "Each visitor gets a unique devotee number on first registration. You may share your certificate freely. Donations are entirely separate and never tied to joining.",
  },
];

const Accordion = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-gold/25 py-5">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={`faq-${idx}`}
        className="w-full flex items-center justify-between text-left gap-4 group"
      >
        <span className="font-serif text-lg md:text-xl text-emerald group-hover:text-gold transition-colors">{q}</span>
        <ChevronDown size={18} className={`text-gold transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-[max-height] duration-500 ${open ? "max-h-60" : "max-h-0"}`}>
        <p className="text-charcoal-muted mt-3 leading-relaxed">{a}</p>
      </div>
    </div>
  );
};

const SuccessModal = ({ devoteeNumber, name, onClose, onRedownload }) => (
  <div className="fixed inset-0 z-50 grid place-items-center px-4 bg-emerald-deep/80 backdrop-blur-sm" data-testid="majlis-success-modal">
    <div className="relative max-w-md w-full bg-ivory rounded-2xl shadow-2xl overflow-hidden">
      <button onClick={onClose} className="absolute top-4 right-4 grid place-items-center h-9 w-9 rounded-full bg-ivory-alt hover:bg-gold/20" aria-label="Close">
        <X size={16} />
      </button>
      <div className="pattern-emerald p-8 text-center">
        <Sparkles className="text-gold mx-auto" size={40} />
        <p className="calligraphy text-gold text-lg mt-3">Ahlan wa Sahlan</p>
        <h2 className="font-serif text-3xl md:text-4xl text-ivory mt-3">Welcome, {name.split(" ")[0]}</h2>
      </div>
      <div className="p-8 text-center">
        <p className="text-charcoal-muted">You are now</p>
        <p className="font-serif text-5xl text-emerald mt-2">
          devotee #<span className="text-gold">{devoteeNumber.toLocaleString()}</span>
        </p>
        <p className="text-sm text-charcoal-muted mt-4 italic">Your certificate has been downloaded. Your faryad is between you and Sarkar — it was not saved anywhere.</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button onClick={onRedownload} data-testid="redownload-cert" className="btn-outline-gold flex-1 inline-flex items-center justify-center gap-2">
            <Download size={16} /> Re-download
          </button>
          <button onClick={onClose} data-testid="modal-close" className="btn-gold flex-1">Continue</button>
        </div>
      </div>
    </div>
  </div>
);

export default function MeccaMajlis() {
  useEffect(() => { document.title = "Mecca Majlis — Join the Sacred Community"; }, []);
  const { count, increment } = useCounter();

  const [form, setForm] = useState({ name: "", faryad: "", email: "", city: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { number, name }

  const onChange = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.faryad.trim()) {
      toast.error("Please share your name and your faryad.");
      return;
    }
    setSubmitting(true);
    const res = await increment();
    const number = res.count || count;
    // Generate certificate client-side (faryad is intentionally not included)
    try {
      await generateCertificate({ name: form.name.trim(), city: form.city.trim(), devoteeNumber: number });
    } catch (err) {
      toast.error("Could not generate your certificate. Please try again.");
      setSubmitting(false);
      return;
    }
    if (res.rateLimited) {
      toast.message("You've already joined recently — counter unchanged.");
    }
    setSuccess({ number, name: form.name.trim(), city: form.city.trim() });
    // Quietly clear faryad so it doesn't linger on screen
    setForm({ name: "", faryad: "", email: "", city: "" });
    setSubmitting(false);
  };

  const redownload = async () => {
    if (!success) return;
    await generateCertificate({ name: success.name, city: success.city, devoteeNumber: success.number });
  };

  return (
    <div data-testid="mecca-majlis-page">
      {/* Hero */}
      <section className="pattern-emerald text-ivory pt-32 pb-20" data-testid="majlis-hero">
        <div className="container-page text-center max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.32em] text-gold">A Community Program</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">Mecca Majlis</h1>
          <p className="calligraphy text-gold text-lg md:text-xl mt-4">A gathering of names. A gathering of hearts.</p>
          <p className="text-ivory/80 mt-6 text-base md:text-lg leading-relaxed">
            Place your name and your faryad before Sarkar's blessed name. Receive a sacred certificate of devotion — instantly, privately, and at no cost.
          </p>
          <div className="mt-10 inline-flex items-center gap-4 px-7 py-4 rounded-2xl bg-ivory/5 backdrop-blur-md border border-gold/30 shadow-2xl">
            <Sparkles size={20} className="text-gold" />
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Devotees Joined</p>
              <p className="font-serif text-3xl md:text-4xl text-ivory mt-1">
                <AnimatedCount value={count} testId="hero-counter" />
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="section-pad" data-testid="majlis-form-section">
        <div className="container-page max-w-2xl">
          <SectionTitle eyebrow="Join the Majlis" title="Place your name before Sarkar" />
          <form onSubmit={submit} data-testid="majlis-form" className="card-soft p-8 md:p-10 space-y-5">
            <div>
              <Label htmlFor="m-name" className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Your Name *</Label>
              <Input id="m-name" data-testid="majlis-name" value={form.name} onChange={onChange("name")} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" placeholder="As you'd like it on the certificate" />
            </div>
            <div>
              <Label htmlFor="m-faryad" className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Your Faryad *</Label>
              <Textarea id="m-faryad" data-testid="majlis-faryad" value={form.faryad} onChange={onChange("faryad")} rows={4} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" placeholder="Speak your heart — this stays between you and Sarkar." />
              <p className="text-[11px] text-charcoal-muted mt-2 italic flex items-center gap-1.5">
                <Lock size={12} className="text-gold" /> Your faryad is not sent to any server. It remains private, in your browser only.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="m-email" className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Email (optional)</Label>
                <Input id="m-email" type="email" data-testid="majlis-email" value={form.email} onChange={onChange("email")} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" />
              </div>
              <div>
                <Label htmlFor="m-city" className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">City (optional)</Label>
                <Input id="m-city" data-testid="majlis-city" value={form.city} onChange={onChange("city")} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" placeholder="Appears on the certificate" />
              </div>
            </div>
            <button type="submit" disabled={submitting} className="btn-gold w-full disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="majlis-submit">
              <Download size={16} />
              {submitting ? "Preparing your certificate..." : "Join the Majlis & Download Certificate"}
            </button>
            <p className="text-xs text-center text-charcoal-muted italic">
              No backend, no database — only the counter ticks. Your details never leave this page.
            </p>
          </form>
        </div>
      </section>

      {/* Community Wall */}
      <section className="pattern-emerald text-ivory section-pad" data-testid="community-wall">
        <div className="container-page">
          <SectionTitle eyebrow="The Living Majlis" title="A Community That Prays Together" light />
          <GeometricDivider />
          <div className="grid md:grid-cols-3 gap-6">
            <div className="rounded-xl border border-gold/30 bg-ivory/5 backdrop-blur p-8 text-center" data-testid="wall-devotees">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Devotees Joined</p>
              <p className="font-serif text-5xl text-ivory mt-3"><AnimatedCount value={count} testId="wall-counter-devotees" /></p>
              <p className="text-ivory/60 text-sm mt-2">in the Mecca Majlis</p>
            </div>
            <div className="rounded-xl border border-gold/30 bg-ivory/5 backdrop-blur p-8 text-center" data-testid="wall-faryads">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Faryads</p>
              <p className="font-serif text-5xl text-ivory mt-3"><AnimatedCount value={count} testId="wall-counter-faryads" /></p>
              <p className="text-ivory/60 text-sm mt-2">placed at the Darbar</p>
            </div>
            <div className="rounded-xl border border-gold/30 bg-ivory/5 backdrop-blur p-8 text-center" data-testid="wall-dua">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Dua</p>
              <p className="font-serif text-5xl text-gold mt-3">24<span className="text-ivory">/</span>7</p>
              <p className="text-ivory/60 text-sm mt-2">continuous, in Sarkar's name</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad" data-testid="majlis-faq">
        <div className="container-page max-w-3xl">
          <SectionTitle eyebrow="Questions of Faith" title="Frequently Asked" />
          <div>
            {FAQS.map((f, i) => <Accordion key={f.q} q={f.q} a={f.a} idx={i} />)}
          </div>
          <p className="text-center text-charcoal-muted text-sm mt-10">
            Have another question? Reach the trust on WhatsApp at <a href={`https://wa.me/${TRUST.whatsapp}`} className="text-gold hover:underline">{TRUST.whatsappDisplay}</a>.
          </p>
        </div>
      </section>

      {success && (
        <SuccessModal
          devoteeNumber={success.number}
          name={success.name}
          onClose={() => setSuccess(null)}
          onRedownload={redownload}
        />
      )}
    </div>
  );
}
